import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { CheckCircle2, XCircle, FileText, Clock } from "lucide-react";
import DashboardLayout from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";

export default function AdminVerifications() {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language === "en" ? "en-US" : i18n.language === "pt" ? "pt-BR" : "es-ES";
  const [items, setItems] = useState([]);
  const [rejecting, setRejecting] = useState(null);
  const [reason, setReason] = useState("");

  const kindLabels = useMemo(() => ({
    identity: t("admin_verifications.kind_identity"),
    professional: t("admin_verifications.kind_professional"),
    company: t("admin_verifications.kind_company"),
    budget: t("admin_verifications.kind_budget"),
    project: t("admin_verifications.kind_project"),
  }), [t]);

  const load = async () => {
    const { data } = await api.get("/api/admin/verifications");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const approve = async (id) => {
    await api.post(`/api/admin/verifications/${id}/approve`);
    toast.success(t("admin_verifications.toast_approved"));
    load();
  };
  const reject = async () => {
    await api.post(`/api/admin/verifications/${rejecting}/reject`, { reason });
    toast.success(t("admin_verifications.toast_rejected"));
    setRejecting(null);
    setReason("");
    load();
  };

  const filtered = (status) => items.filter((v) => v.status === status);
  const emptyLabels = {
    pending: t("admin_verifications.empty_pending"),
    approved: t("admin_verifications.empty_approved"),
    rejected: t("admin_verifications.empty_rejected"),
  };

  return (
    <DashboardLayout title={t("admin_verifications.title")} subtitle={t("admin_verifications.subtitle")}>
      <Tabs defaultValue="pending">
        <TabsList className="bg-slate-100 rounded-full mb-6">
          <TabsTrigger value="pending" data-testid="tab-pending">{t("admin_verifications.tab_pending", { count: filtered("pending").length })}</TabsTrigger>
          <TabsTrigger value="approved" data-testid="tab-approved">{t("admin_verifications.tab_approved", { count: filtered("approved").length })}</TabsTrigger>
          <TabsTrigger value="rejected" data-testid="tab-rejected">{t("admin_verifications.tab_rejected", { count: filtered("rejected").length })}</TabsTrigger>
        </TabsList>

        {["pending", "approved", "rejected"].map((status) => (
          <TabsContent key={status} value={status} className="space-y-3">
            {filtered(status).length === 0 && (
              <div className="kpi-card p-8 text-center text-slate-500">{emptyLabels[status]}</div>
            )}
            {filtered(status).map((v) => (
              <div key={v.id} className="kpi-card p-5" data-testid={`admin-verification-${v.id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <Badge className="bg-gold/15 text-gold hover:bg-gold/15 border-0">{kindLabels[v.kind] || v.kind}</Badge>
                      <span className="text-xs text-slate-500">{t("admin_verifications.user_id_prefix", { id: v.user_id.slice(0, 8) })}</span>
                    </div>
                    {v.notes && <p className="text-sm text-slate-700 mt-2">{v.notes}</p>}
                    {v.reason && <p className="text-xs text-red-600 mt-2">{t("admin_verifications.reject_reason_prefix", { reason: v.reason })}</p>}
                    <p className="text-xs text-slate-400 mt-1">{new Date(v.created_at).toLocaleString(dateLocale)}</p>
                    {v.documents?.length ? (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {v.documents.map((d, i) => (
                          <a key={i} href={d.url} target="_blank" rel="noreferrer" className="text-xs inline-flex items-center gap-1 text-petroleum hover:text-gold border border-slate-200 rounded-full px-3 py-1">
                            <FileText size={10} /> {d.name}
                          </a>
                        ))}
                      </div>
                    ) : null}
                  </div>
                  {status === "pending" && (
                    <div className="flex flex-col gap-2">
                      <Button size="sm" onClick={() => approve(v.id)} data-testid={`button-approve-${v.id}`} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                        <CheckCircle2 size={12} className="mr-1" /> {t("admin_verifications.approve")}
                      </Button>
                      <Dialog open={rejecting === v.id} onOpenChange={(o) => !o && setRejecting(null)}>
                        <DialogTrigger asChild>
                          <Button size="sm" variant="outline" onClick={() => setRejecting(v.id)} data-testid={`button-reject-${v.id}`} className="text-red-600 border-red-200">
                            <XCircle size={12} className="mr-1" /> {t("admin_verifications.reject")}
                          </Button>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader><DialogTitle>{t("admin_verifications.reject_dialog_title")}</DialogTitle></DialogHeader>
                          <Textarea rows={4} value={reason} onChange={(e) => setReason(e.target.value)} placeholder={t("admin_verifications.reject_placeholder")} />
                          <DialogFooter>
                            <Button variant="ghost" onClick={() => setRejecting(null)}>{t("common.cancel")}</Button>
                            <Button onClick={reject} className="bg-red-600 hover:bg-red-700 text-white">{t("admin_verifications.reject")}</Button>
                          </DialogFooter>
                        </DialogContent>
                      </Dialog>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </TabsContent>
        ))}
      </Tabs>
    </DashboardLayout>
  );
}
